/**
 * Inline SVG icon set, lifted from `design_handoff/mvp/_shell.html` and
 * `design_handoff/mvp/app-shell.html` so the shell visual matches the
 * design 1:1 without a dependency on an icon library.
 *
 * Each icon renders the same `viewBox="0 0 24 24"` paths the design
 * uses. `currentColor` lets the icon inherit the surrounding
 * `.side-item` / `.icon-btn` color states.
 *
 * Default width/height attributes are critical: when an icon is used
 * outside a sized container (e.g. the topbar user-menu caret), an SVG
 * without explicit dimensions and without a CSS class that sizes it
 * would render at Chromium's 300x150 default — the bug that forced the
 * topbar to ~150px on every page. Inline width/height are presentation
 * attributes, so any matching CSS rule (`.icon-btn svg`,
 * `.side-item .ic`, `.search .sicon`, etc.) still wins.
 */

interface IconProps {
  readonly className?: string;
  readonly title?: string;
  readonly size?: number;
}

function svg(props: IconProps, children: JSX.Element) {
  // `||` (not `??`) so an explicit `className=""` from a caller still
  // falls back to `ic` rather than sending an empty class through.
  const cls = props.className || 'ic';
  const size = props.size ?? 16;
  return (
    <svg
      className={cls}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      role={props.title ? 'img' : 'presentation'}
      aria-label={props.title}
    >
      {children}
    </svg>
  );
}

export const HomeIcon = (p: IconProps) => svg(p, <path d="M3 12L12 4l9 8M5 10v10h14V10" />);
export const ClientsIcon = (p: IconProps) =>
  svg(p, <path d="M3 21v-2a4 4 0 014-4h6a4 4 0 014 4v2M9 11a4 4 0 100-8 4 4 0 000 8z" />);
export const BellIcon = (p: IconProps) =>
  svg(p, <path d="M6 8a6 6 0 1112 0c0 7 3 7 3 9H3c0-2 3-2 3-9zM10 21a2 2 0 004 0" />);
export const OverviewIcon = (p: IconProps) => svg(p, <path d="M4 6h16M4 12h16M4 18h10" />);
export const StrategyIcon = (p: IconProps) =>
  svg(
    p,
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3 2" />
    </>,
  );
export const ContentIcon = (p: IconProps) =>
  svg(
    p,
    <>
      <rect x="4" y="4" width="16" height="16" rx="2" />
      <path d="M8 10h8M8 14h5" />
    </>,
  );
export const PublishIcon = (p: IconProps) =>
  svg(p, <path d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z" />);
export const PaidMediaIcon = (p: IconProps) => svg(p, <path d="M3 3v18h18M7 14l4-4 4 4 5-6" />);
export const CreativeIcon = (p: IconProps) =>
  svg(
    p,
    <>
      <rect x="3" y="3" width="18" height="18" rx="2" />
      <circle cx="9" cy="9" r="2" />
      <path d="M21 15l-5-5L5 21" />
    </>,
  );
export const InfluencersIcon = (p: IconProps) =>
  svg(
    p,
    <>
      <circle cx="9" cy="8" r="4" />
      <path d="M3 21v-1a6 6 0 016-6h0a6 6 0 016 6v1M17 11l2 2 4-4" />
    </>,
  );
export const ReportsIcon = (p: IconProps) =>
  svg(
    p,
    <>
      <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
      <path d="M14 2v6h6M9 13h6M9 17h6" />
    </>,
  );
export const CrmIcon = (p: IconProps) =>
  svg(
    p,
    <path d="M21 11.5a8.38 8.38 0 01-.9 3.8 8.5 8.5 0 01-7.6 4.7 8.38 8.38 0 01-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 01-.9-3.8 8.5 8.5 0 014.7-7.6 8.38 8.38 0 013.8-.9h.5a8.48 8.48 0 018 8v.5z" />,
  );
export const TeamIcon = (p: IconProps) =>
  svg(
    p,
    <>
      <path d="M16 21v-2a4 4 0 00-4-4H6a4 4 0 00-4 4v2M9 11a4 4 0 100-8 4 4 0 000 8z" />
      <path d="M22 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75" />
    </>,
  );
export const SettingsIcon = (p: IconProps) =>
  svg(
    p,
    <>
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 11-2.83 2.83l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 11-4 0v-.09a1.65 1.65 0 00-1-1.51 1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 11-2.83-2.83l.06-.06a1.65 1.65 0 00.33-1.82 1.65 1.65 0 00-1.51-1H3a2 2 0 110-4h.09a1.65 1.65 0 001.51-1 1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 112.83-2.83l.06.06a1.65 1.65 0 001.82.33h.01a1.65 1.65 0 001-1.51V3a2 2 0 114 0v.09a1.65 1.65 0 001 1.51h.01a1.65 1.65 0 001.82-.33l.06-.06a2 2 0 112.83 2.83l-.06.06a1.65 1.65 0 00-.33 1.82v.01a1.65 1.65 0 001.51 1H21a2 2 0 110 4h-.09a1.65 1.65 0 00-1.51 1z" />
    </>,
  );
export const SearchIcon = (p: IconProps) =>
  svg(
    p,
    <>
      <circle cx="11" cy="11" r="7" />
      <path d="M21 21l-4-4" />
    </>,
  );
export const HelpIcon = (p: IconProps) =>
  svg(
    p,
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M9.5 9.5a2.5 2.5 0 015 0c0 1.5-2.5 2-2.5 4M12 17h.01" />
    </>,
  );
export const CaretDownIcon = (p: IconProps) => svg(p, <path d="M7 10l5 5 5-5" />);
export const SignOutIcon = (p: IconProps) =>
  svg(p, <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4M16 17l5-5-5-5M21 12H9" />);
export const ProfileIcon = (p: IconProps) =>
  svg(
    p,
    <>
      <circle cx="12" cy="8" r="4" />
      <path d="M4 21v-1a6 6 0 016-6h4a6 6 0 016 6v1" />
    </>,
  );
export const ShieldIcon = (p: IconProps) =>
  svg(p, <path d="M12 2l8 4v6c0 5-3.5 9-8 10-4.5-1-8-5-8-10V6l8-4z" />);
